import { useSubmitCsrRequestMutation } from '../../api/csrRequest.api';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../redux/slices/alertSlice';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { GridApiPro } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSelectedRecords } from '../../redux/slices/selectedGridRecordsSlice';
import type { RootState } from '../../redux/store';
import RequestModal from '../Modals/RequestModal/RequestModal.tsx';
import DataGridToolbarActionsComponent from './DataGridToolbar/DataGridToolbarActionsComponent.tsx';
import type { DataGridType } from './DataGridTypes.ts';
import { CSR_REQUEST_SUCCESS, CSR_REQUEST_ERROR } from '../../constants/messages.tsx';

interface DataGridComponentProps {
	apiRef: React.RefObject<GridApiPro | null>;
	dataGridType: DataGridType;
	onRefresh?: () => void;
}

const DataGridToolbarComponent: React.FC<DataGridComponentProps> = ({
	apiRef,
	dataGridType,
	onRefresh,
}) => {
	// Get selected rows from store using custom hook
	const selectedRows = useSelector((state: RootState) => selectSelectedRecords(state));
	const [requestModalOpen, setRequestModalOpen] = useState(false);
	const [submitCsrRequest, { isLoading }] = useSubmitCsrRequestMutation();
	const dispatch = useDispatch();
	const [submitted, setSubmitted] = useState(false);

	const handleRequestClick = () => {
		setRequestModalOpen(true);
	};

	const handleRequestClose = () => {
		setRequestModalOpen(false);
	};

	const handleRequestSubmit = async (payload: any) => {
		setSubmitted(true);
		try {
			await submitCsrRequest(payload).unwrap();
			setRequestModalOpen(false);
			dispatch(
				showAlert({
					message: CSR_REQUEST_SUCCESS,
					type: 'success',
				})
			);
		} catch (err: any) {
			dispatch(
				showAlert({
					message: err?.data?.message || CSR_REQUEST_ERROR,
					type: 'error',
				})
			);
		} finally {
			setSubmitted(false);
		}
	};

	const renderToolbarActions = () => {
		return (
			<DataGridToolbarActionsComponent
				apiRef={apiRef}
				dataGridType={dataGridType}
				title={dataGridType.title}
				handleRequestClick={handleRequestClick}
				onRefresh={onRefresh}
			/>
		);
	};

	return (
		<>
			<Box
				sx={{
					height: dataGridType.searchBox ? '50px' : 'auto',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					backgroundColor: (theme) => theme.palette.surface.tertiary,
					px: 2,
					py: 1,
					borderRadius: 1,
					mb: 1,
					borderTop: '1px solid',
					borderColor: (theme) => theme.palette.divider,
				}}
			>
				<Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
					{dataGridType.title}
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>{renderToolbarActions()}</Box>
			</Box>
			<RequestModal
				open={requestModalOpen}
				onClose={handleRequestClose}
				onSubmit={handleRequestSubmit}
				selectedRecords={selectedRows}
				requestTypes={dataGridType.requestTypes || []}
				submitDisabled={isLoading || submitted}
			/>
		</>
	);
};

export default DataGridToolbarComponent;
